import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { ReactComponent as InvoiceIcon } from '@/assets/icons/invoice.svg';
import { ReactComponent as ReceiverIcon } from '@/assets/icons/receiver.svg';
import { ReactComponent as ResendIcon } from '@/assets/icons/refresh-icon.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { ReactComponent as DeclineIcon } from '@/assets/icons/x-circle.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import BackboneUserBadge from '@/components/BackboneUserBadge';
import { Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { InvoiceResource } from '@/types/resources';
import { api } from '@/utils/api';
import { countryListNotRevolut } from '@/utils/data/payments';
import { InvoiceSource, InvoiceStatus, PaymentMethod } from '@/utils/enums';
import { renderCountryName, renderInvoiceAmount } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import { isInvoiceSelectable } from '../utils';
import ApproveInvoiceCheckbox from './ApproveInvoiceCheckbox';
import InvoiceMark from './InvoiceMark';
import InvoicesChangeReceiverModal from './InvoicesChangeReceiverModal';
import InvoicesEditModal from './InvoicesEditModal';
import InvoicesFileBlock from './InvoicesFileBlock';
import InvoicesMemberModal from './InvoicesMemberModal';
import InvoicesRejectModal from './InvoicesRejectModal';
import InvoicesStatusDropdown from './InvoicesStatusDropdown';
import { Checkbox, CheckboxHolder, CheckboxLabel } from './InvoicesTable';
import InvoicesWarning from './InvoicesWarning';
import PaymentMethodIcon from './PaymentMethodIcon';

const BordersHolder = styled.div`
  left: 0;
  top: 0;
  height: 100%;
  position: absolute;
  display: flex;
`;

export const LeftBorder = styled.span<{ $isBank?: boolean; $isUser?: boolean }>`
  border-radius: 0px 2px 2px 0px;
  width: 3px;
  height: 100%;
  ${({ $isBank }) =>
    $isBank &&
    css`
      background: ${({ theme }) => theme.colors.info};
    `}
  ${({ $isUser }) =>
    $isUser &&
    css`
      background: ${({ theme }) => theme.colors.error};
    `}
`;

export const CheckboxPlaceholder = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.lightGray};
  cursor: not-allowed;
`;

export const ReceiverWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: center;
`;

const Badges = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 8px;
`;

const ReceiverName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const ProformaBadge = styled(Text)`
  padding: 0 6px;
  height: 16px;
  line-height: 16px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.postProductionLight};
  color: ${({ theme }) => theme.colors.postProduction};
`;

const Files = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Boxes = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

interface Props {
  invoice: InvoiceResource;
  selectedInvoices: number[];
  canManageData: boolean;
  // Whether the table has an Actions column; the row then always renders the
  // cell, empty when this invoice has no actions.
  showActionsColumn: boolean;
  // Unpaid invoices (Pre-received and Received) have to be approved before they
  // can be selected, paid, or have their status changed.
  requiresApproval: boolean;
  setSelectedInvoices: React.Dispatch<React.SetStateAction<number[]>>;
}

const InvoicesTableRow: FC<Props> = ({
  invoice,
  selectedInvoices,
  canManageData,
  showActionsColumn,
  requiresApproval,
  setSelectedInvoices,
}) => {
  // PayPal and Payoneer invoices are settled by email, so bank-detail checks
  // don't apply to either.
  const isWalletInvoice = invoice.payment_method !== PaymentMethod.Bank;

  const [showMemberModal, setShowMemberModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);

  const [showChangeReceiverModal, setShowChangeReceiverModal] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);

  const [isResending, setIsResending] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews['invoicesTable'],
  );

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this invoice?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/payments/invoices/${invoice.id}`,
      );

      queryClient.invalidateQueries(['invoices_list'], {
        exact: false,
      });

      alerts.success('Invoice was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  // Asks the backend to build the invoice document again and send it to the same
  // recipient, from the sender it was originally sent from. Whether that is
  // allowed at all is the backend's call — see `can_resend`.
  const onResend = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to regenerate this invoice and send it again?',
      );

      if (!confirm) return;

      setIsResending(true);

      await api.post(`/v1/payments/invoices/${invoice.id}/resend`);

      queryClient.invalidateQueries(['invoices_list'], {
        exact: false,
      });

      alerts.success('Invoice was regenerated and sent again');
    } catch (e) {
      alerts.error();
    } finally {
      setIsResending(false);
    }
  };

  const isOverAmount = () => {
    if (invoice.gpm) {
      if (invoice.amount * 0.85 >= 5000) {
        return <InvoiceMark type="5k" />;
      }
      if (invoice.amount * 0.85 >= 1000) {
        return <InvoiceMark type="1k" />;
      }
    } else {
      if (invoice.amount >= 5000) {
        return <InvoiceMark type="5k" />;
      }
      if (invoice.amount >= 1000) {
        return <InvoiceMark type="1k" />;
      }
    }
  };

  const checkIfRevolutOkay = () => {
    if (
      countryListNotRevolut.find(
        (country) => country['alpha-2'] === invoice.bank.bank_country,
      )
    ) {
      return false;
    }
    return true;
  };

  const CheckIfSwedbankOk = () => {
    if (!!invoice.bank.bank && !invoice.bank.iban && !isWalletInvoice) {
      return false;
    }
    return true;
  };

  const checkIfBankDetailsExist = () => {
    if (isWalletInvoice) return true;

    if (invoice.bank.bank && invoice.bank.iban && invoice.bank.swift_code) {
      return true;
    }
    return false;
  };

  const checkIfUserDetailsExist = () => {
    if (isWalletInvoice) return true;

    if (invoice.member.name) {
      return true;
    }
    return false;
  };

  const isApproved = Boolean(invoice.approved_at);

  // On the unpaid tabs an invoice must be approved before it can be selected
  // (for export/status changes).
  const isSelectable = isInvoiceSelectable(invoice, requiresApproval);

  // Coloured left-edge warning bars for missing bank/user details. They live in
  // whichever is the row's first (sticky) cell — the selection column for
  // managers, otherwise the Receiver cell.
  const borders = (
    <BordersHolder>
      {!checkIfBankDetailsExist() && <LeftBorder $isBank />}
      {!checkIfUserDetailsExist() && <LeftBorder $isUser />}
    </BordersHolder>
  );

  return (
    <tr>
      {canManageData && (
        <Td>
          {borders}
          <Boxes>
            {isSelectable ? (
              <CheckboxHolder>
                <Checkbox
                  id={`invoice-${invoice.id}`}
                  name="invoices"
                  type="checkbox"
                  checked={selectedInvoices.includes(invoice.id)}
                  onChange={() => {
                    selectedInvoices.includes(invoice.id)
                      ? setSelectedInvoices((prev) =>
                          prev.filter((item) => item !== invoice.id),
                        )
                      : setSelectedInvoices((prev) => [...prev, invoice.id]);
                  }}
                />
                <CheckboxLabel htmlFor={`invoice-${invoice.id}`} />
              </CheckboxHolder>
            ) : (
              <CheckboxPlaceholder />
            )}
            <ApproveInvoiceCheckbox
              invoice={invoice}
              interactive={requiresApproval}
            />
          </Boxes>
        </Td>
      )}
      <Td>
        {!canManageData && borders}
        <ReceiverWrapper>
          <PaymentMethodIcon method={invoice.payment_method} />
          <Badges>
            {!checkIfRevolutOkay() && <InvoiceMark type="revolut" />}
            {!CheckIfSwedbankOk() && <InvoiceMark type="swedbank" />}
            {!!invoice.gpm && <InvoiceMark type="gpm" />}
            {isOverAmount()}
            {!checkIfBankDetailsExist() && <InvoicesWarning type="bank" />}
            {!checkIfUserDetailsExist() && <InvoicesWarning type="user" />}
            {invoice.source === InvoiceSource.Freelancer && (
              <BackboneUserBadge type="generated" />
            )}
            {invoice.source === InvoiceSource.Import && (
              <BackboneUserBadge type="imported" />
            )}
          </Badges>
          <ReceiverName>
            <Text $type="smallHeadline">{invoice.member.name}</Text>
            {invoice.is_proforma && (
              <ProformaBadge $type="extraSmall">Proforma</ProformaBadge>
            )}
          </ReceiverName>
        </ReceiverWrapper>
      </Td>

      {tableViewOptions['True Receiver'] && (
        <Td>{invoice.true_receiver ?? '-'}</Td>
      )}
      {tableViewOptions.Comments && <Td>{invoice.comment ?? '-'}</Td>}
      {tableViewOptions['Freelancer Notes'] && (
        <Td>{invoice.freelancer_notes ?? '-'}</Td>
      )}

      <>
        {tableViewOptions['Debtor Bank'] && (
          <Td>{invoice.bank.debtor_bank ?? '-'}</Td>
        )}
        {tableViewOptions['Creditor Bank'] && (
          <Td>
            {invoice.bank.bank
              ? `${invoice.bank.bank} · ${invoice.bank.iban.substring(
                  invoice.bank.iban.length - 4,
                )}`
              : '-'}
          </Td>
        )}
        {tableViewOptions['Creditor Bank Country'] && (
          <Td>
            {invoice.bank.bank_country
              ? renderCountryName(invoice.bank.bank_country)
              : '-'}
          </Td>
        )}
        {tableViewOptions['Payment Type'] && (
          <Td>{invoice.bank.payment_type ?? '-'}</Td>
        )}
      </>
      {tableViewOptions.Department && (
        <Td>{invoice.department?.name ?? '-'}</Td>
      )}
      {tableViewOptions.Company && <Td>{invoice.company?.name ?? '-'}</Td>}
      {tableViewOptions['Invoice ID'] && <Td>{invoice.external_id}</Td>}
      {tableViewOptions['Finance Account Number'] && (
        <Td>
          {invoice.finance_account_number_data
            ? invoice.finance_account_number_data.name
            : '-'}
        </Td>
      )}
      {tableViewOptions['Amount (€)'] && (
        <Td>
          {invoice.currency === 'EUR'
            ? renderInvoiceAmount(invoice.amount, !!invoice.gpm)
            : '-'}
        </Td>
      )}
      {tableViewOptions['Amount ($)'] && (
        <Td>
          {invoice.currency === 'USD'
            ? renderInvoiceAmount(invoice.amount, !!invoice.gpm)
            : '-'}
        </Td>
      )}
      {tableViewOptions['Amount (£)'] && (
        <Td>
          {invoice.currency === 'GBP'
            ? renderInvoiceAmount(invoice.amount, !!invoice.gpm)
            : '-'}
        </Td>
      )}
      {tableViewOptions['Payment Purpose'] && (
        <Td>{invoice.payment_purpose ?? '-'}</Td>
      )}
      {tableViewOptions['Received Date'] && (
        <Td>{DateTime.fromISO(invoice.created_at).toFormat('dd/LL/yyyy t')}</Td>
      )}
      {tableViewOptions['Paid Date'] && (
        <Td>
          {invoice.paid_at
            ? DateTime.fromISO(invoice.paid_at).toFormat('dd/LL/yyyy t')
            : '-'}
        </Td>
      )}
      {tableViewOptions['Created By'] && <Td>{invoice.creator ?? '-'}</Td>}
      {tableViewOptions['Last Edited'] && <Td>{invoice.edited_by ?? '-'}</Td>}
      {tableViewOptions.Files && (
        <Td>
          <Files>
            {invoice.file ? (
              <InvoicesFileBlock
                invoice={invoice}
                invoiceId={invoice.id}
                file={invoice.file}
              />
            ) : (
              '-'
            )}
            {invoice.original_file && (
              <InvoicesFileBlock
                isOriginal
                invoice={invoice}
                invoiceId={invoice.id}
                file={invoice.original_file}
              />
            )}
          </Files>
        </Td>
      )}
      {tableViewOptions['Proforma Files'] && (
        <Td>
          <Files>
            {invoice.proforma_file ? (
              <InvoicesFileBlock
                isProforma
                invoice={invoice}
                invoiceId={invoice.id}
                file={invoice.proforma_file}
              />
            ) : (
              '-'
            )}
            {invoice.original_proforma_file && (
              <InvoicesFileBlock
                isOriginal
                isProforma
                invoice={invoice}
                invoiceId={invoice.id}
                file={invoice.original_proforma_file}
              />
            )}
          </Files>
        </Td>
      )}
      {tableViewOptions['Additional Files'] && (
        <Td>
          <Files>
            {invoice.additional_files?.length ||
            invoice.original_additional_files?.length ? (
              <>
                {invoice.additional_files?.map((file) => (
                  <InvoicesFileBlock
                    key={file.id}
                    invoice={invoice}
                    invoiceId={invoice.id}
                    file={file}
                    downloadUrl={file.versions.original}
                  />
                ))}
                {invoice.original_additional_files?.map((file) => (
                  <InvoicesFileBlock
                    key={file.id}
                    isOriginal
                    invoice={invoice}
                    invoiceId={invoice.id}
                    file={file}
                    downloadUrl={file.versions.original}
                  />
                ))}
              </>
            ) : (
              '-'
            )}
          </Files>
        </Td>
      )}
      {canManageData && tableViewOptions.Status && (
        <Td>
          {invoice.status !== undefined && invoice.status !== null && (
            <InvoicesStatusDropdown
              invoiceId={invoice.id}
              status={invoice.status}
              disabled={requiresApproval && !isApproved}
            />
          )}
        </Td>
      )}
      {showActionsColumn && (
        <Td $actionsCol>
          {(canManageData || invoice.status !== InvoiceStatus.Paid) && (
            <ActionsDropdown>
              {canManageData && (
                <ActionsDropdownButton
                  as="button"
                  onClick={() => setShowMemberModal(true)}
                >
                  <span>
                    <ReceiverIcon />
                  </span>
                  Edit member
                </ActionsDropdownButton>
              )}
              {canManageData && (
                <ActionsDropdownButton
                  as="button"
                  onClick={() => setShowChangeReceiverModal(true)}
                >
                  <span>
                    <ReceiverIcon />
                  </span>
                  Change Receiver & Type
                </ActionsDropdownButton>
              )}
              {(canManageData || invoice.status !== InvoiceStatus.Paid) && (
                <ActionsDropdownButton
                  as="button"
                  onClick={() => setShowEditModal(true)}
                >
                  <span>
                    <InvoiceIcon />
                  </span>
                  Edit invoice
                </ActionsDropdownButton>
              )}
              {canManageData && invoice.can_resend && (
                <ActionsDropdownButton
                  as="button"
                  onClick={onResend}
                  disabled={isResending}
                >
                  <span>
                    <ResendIcon />
                  </span>
                  Regenerate and Resend
                </ActionsDropdownButton>
              )}
              {canManageData &&
                invoice.source === InvoiceSource.Freelancer &&
                invoice.status === InvoiceStatus.Received && (
                  <ActionsDropdownButton
                    as="button"
                    onClick={() => setShowRejectModal(true)}
                  >
                    <span>
                      <DeclineIcon />
                    </span>
                    Request Changes
                  </ActionsDropdownButton>
                )}
              {canManageData && (
                <ActionsDropdownButton as="button" onClick={onDelete}>
                  <span>
                    <TrashIcon />
                  </span>
                  Delete
                </ActionsDropdownButton>
              )}
            </ActionsDropdown>
          )}
          <AnimatePresence>
            {showMemberModal && (
              <InvoicesMemberModal
                onClose={() => setShowMemberModal(false)}
                memberId={invoice.member_id}
              />
            )}
            {showChangeReceiverModal && (
              <InvoicesChangeReceiverModal
                onClose={() => setShowChangeReceiverModal(false)}
                invoice={invoice}
              />
            )}
            {showEditModal && (
              <InvoicesEditModal
                onClose={() => setShowEditModal(false)}
                invoice={invoice}
              />
            )}
            {showRejectModal && (
              <InvoicesRejectModal
                invoiceId={invoice.id}
                onClose={() => setShowRejectModal(false)}
              />
            )}
          </AnimatePresence>
        </Td>
      )}
    </tr>
  );
};

export default InvoicesTableRow;
